import dayjs from 'dayjs'

import type {
  AgreementAuthorizedEntityResponse,
  AgreementCommitmentResponse,
  AgreementCreateParams,
  AgreementDiscountCreateParams,
  AgreementDiscountResponse,
  AgreementDiscountTierCreateParams,
  AgreementProductResponse,
  AgreementResponse,
  ProductResponse
} from '~/data/api/client/billing'
import { parseFromSmallestDenomination, parseToSmallestDenomination } from '~/data/utils/currency'
import type { TzDayjs } from '~/hooks/useUserPrefs'

import type { ValidatedAgreementFormData } from './components/schema'
import {
  type AgreementCategoryV1,
  type AgreementFormData,
  type AgreementProductFormData,
  type AuthorizedEntityFormData,
  type BillingCycleV1,
  DEFAULT_COURTESY_PERCENT,
  DEFAULT_COURTESY_WEEK_START_DAY,
  DEFAULT_DEMAND_CHARGE_ALLOCATION_METHOD,
  DEFAULT_DEMAND_CHARGE_PASS_THROUGH_PERCENTAGE,
  DEFAULT_PEAK_SAVER_QUANTITY_KW,
  DEFAULT_PEAK_SAVER_SAVE_TYPE,
  ENTITY_COUNT_LEVEL_AUTOMATIC,
  type MetadataEntryFormData,
  TIER_QUANTITY_SOURCE_PRICE_DEFAULT,
  courtesyQuantityFor,
  createEmptyAgreementProductLine,
  getForcedAutoCharge,
  getForcedCollectionPolicy,
  getForcedLineItemGrouping
} from './types'

/**
 * A NumberField keeps what was typed as a string until the form resolver casts it, and the billing composer
 * snapshots the raw form, so a courtesy quantity or percentage has to read either way. Blank and non-numeric
 * values read as absent.
 */
export function toFiniteNumber(value: unknown): number | undefined {
  const parsed = typeof value === 'string' && value.trim() !== '' ? Number(value) : value
  return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : undefined
}

export function parseMetadataEntriesToObject(
  entries: MetadataEntryFormData[] | undefined
): Record<string, string> | undefined {
  const metadata = Object.fromEntries(
    (entries ?? []).map(({ key, value }) => [key.trim(), value] as const).filter(([key]) => key.length > 0)
  )
  return Object.keys(metadata).length > 0 ? metadata : undefined
}

export function parseMetadataObjectToEntries(metadata?: Record<string, string>): MetadataEntryFormData[] {
  const entries = Object.entries(metadata ?? {}).map(([key, value]) => ({ key, value }))
  return entries.length > 0 ? entries : [{ key: '', value: '' }]
}

type ParsedAgreementFormData = Omit<AgreementCreateParams, 'organizationId'>

type ParseAgreementFormDataOptions = {
  tzDayjs?: TzDayjs
  /**
   * Whether the payload targets an update (edit flow) or a create. Updates must send
   * `peakSaverConfiguration: { enabled: false }` explicitly when the toggle is off — the API
   * leaves an omitted config unchanged. Creates omit the object instead, so no disabled
   * configuration record is persisted.
   */
  intent?: 'create' | 'update'
}

function toStartOfDayIso(value: string, tzDayjs?: TzDayjs): string {
  return (tzDayjs ?? dayjs)(value).startOf('day').toISOString()
}

/**
 * Converts AgreementFormData to AgreementCreateParams
 */
export function parseAgreementFormData(
  data: ValidatedAgreementFormData,
  { tzDayjs, intent = 'create' }: ParseAgreementFormDataOptions = {}
): ParsedAgreementFormData {
  // Parse authorized entities
  const authorizedEntities = (function (): AuthorizedEntityFormData[] {
    const { authorizedFleets, authorizedVehicles, authorizedUsers, authorizedUserGroups, authorizedAuthTags } = data

    const authTagEntities = (authorizedAuthTags || []).map((entityId) => ({
      entityId,
      entityType: 'AUTH_TAG' as const
    }))

    let entitiesToAuthorize: AuthorizedEntityFormData[] = []

    if (data.category === 'DRIVER') {
      // For Driver category, only include users, user groups, and auth tags
      entitiesToAuthorize = [
        ...(authorizedUsers || []).map((entityId) => ({
          entityId,
          entityType: 'USER' as const
        })),
        ...(authorizedUserGroups || []).map((entityId) => ({
          entityId,
          entityType: 'USER_GROUP' as const
        })),
        ...authTagEntities
      ]
    } else if (data.category === 'FLEET') {
      // For Fleet category, include all entity types
      entitiesToAuthorize = [
        ...(authorizedFleets || []).map((entityId) => ({
          entityId,
          entityType: 'FLEET' as const
        })),
        ...(authorizedVehicles || []).map((entityId) => ({
          entityId,
          entityType: 'VEHICLE' as const
        })),
        ...(authorizedUsers || []).map((entityId) => ({
          entityId,
          entityType: 'USER' as const
        })),
        ...(authorizedUserGroups || []).map((entityId) => ({
          entityId,
          entityType: 'USER_GROUP' as const
        })),
        ...authTagEntities
      ]
    }

    return entitiesToAuthorize
  })()

  // Parse Discounts. A COURTESY discount's period quantities become one tier per period (start 0, end = the
  // quantity, amount = the percentage), which is how billing-api stores and rates them.
  const discounts: AgreementDiscountCreateParams[] | undefined = data.volumeDiscountEnabled
    ? data.discounts.map((discount) => {
        const application = discount.application || 'VOLUME'
        const quantityUnit = discount.quantityUnit || 'KWH'
        if (application === 'COURTESY') {
          const percent = toFiniteNumber(discount.courtesyPercent) ?? DEFAULT_COURTESY_PERCENT
          const dailyQuantity = toFiniteNumber(discount.courtesyDailyQuantity)
          const weeklyQuantity = toFiniteNumber(discount.courtesyWeeklyQuantity)
          const tiers: AgreementDiscountTierCreateParams[] = []
          if (dailyQuantity !== undefined) {
            tiers.push({ startQuantity: 0, endQuantity: dailyQuantity, discountAmount: percent, period: 'DAY' })
          }
          if (weeklyQuantity !== undefined) {
            tiers.push({ startQuantity: 0, endQuantity: weeklyQuantity, discountAmount: percent, period: 'WEEK' })
          }
          return {
            name: discount.name!,
            discountType: 'PERCENTAGE' as const,
            application,
            quantityUnit,
            weekStartDay: discount.courtesyWeekStartDay ?? DEFAULT_COURTESY_WEEK_START_DAY,
            applicableProductIds: [discount.productId!],
            tiers
          }
        }
        return {
          name: discount.name!,
          discountType: 'PERCENTAGE' as const,
          application,
          quantityUnit,
          applicableProductIds: [discount.productId!],
          tiers: discount.tiers.map((tier) => ({
            startQuantity: tier.usageAmountStart!,
            endQuantity: tier.usageAmountEnd!,
            discountAmount: tier.discountAmount!
          }))
        }
      })
    : undefined

  // Parse Commitments
  const commitments = data.usageCommitmentEnabled
    ? data.commitments.map((commitment) => ({
        name: commitment.name!,
        commitmentType: 'USAGE' as const,
        usageQuantity: commitment.usageAmount!,
        costAmount: commitment.rateAboveCommitment!,
        productIds: [commitment.productId!]
      }))
    : undefined

  // Forced ATOMIC (REAL_TIME) only matters on updates with a stored grouping: an omitted field on
  // update would leave a stored period-cycle grouping in place and fail the API's grouping/cycle
  // validation. When nothing is stored (''), omit the field instead of backfilling ATOMIC — the
  // update API can't clear the field later, so an explicit ATOMIC would permanently override the
  // template grouping / SITE_PRICE default if the agreement ever moves back to a period cycle.
  // Creates can always simply omit the field.
  const forcedLineItemGrouping = getForcedLineItemGrouping(data.billingCycle)
  const lineItemGrouping = forcedLineItemGrouping
    ? intent === 'update' && data.lineItemGrouping
      ? forcedLineItemGrouping
      : undefined
    : data.lineItemGrouping || undefined

  return {
    partnerOrganizationId: data.partnerOrganizationId,
    name: data.name,
    startDate: dayjs(data.startDate).isValid() ? toStartOfDayIso(data.startDate, tzDayjs) : data.startDate,
    endDate: data.endDate && dayjs(data.endDate).isValid() ? dayjs(data.endDate).toISOString() : undefined, // The API returns null for endDate, but the spec doesn't say that
    billingCycle: data.billingCycle as AgreementCreateParams['billingCycle'],
    collectionPolicy: getForcedCollectionPolicy(data.billingCycle) ?? data.collectionPolicy,
    lineItemGrouping,
    autoCharge: getForcedAutoCharge(data.billingCycle) ?? data.autoCharge,
    // REAL_TIME invoices have no period close to hold at, and the API defaults an omitted value to 1.
    reviewFirstInvoices: data.billingCycle === 'REAL_TIME' ? 0 : data.reviewFirstInvoices,
    invoiceDueDays: data.invoiceDueDays,
    billingAnchorDayOfMonth: data.billingAnchorDayOfMonth,
    billingAnchorDayOfWeek: data.billingAnchorDayOfWeek,
    authorizationChargeEnabled: data.authorizationChargeEnabled,
    authorizationCharge:
      data.authorizationChargeEnabled && data.authorizationCharge
        ? Math.round(parseToSmallestDenomination(data.authorizationCharge))
        : undefined,
    peakSaverEnabled: data.peakSaverEnabled,
    // The legacy boolean stays for compatibility; the configuration object is the source of truth for detailed
    // settings. When the toggle is off, updates must send enabled: false explicitly (an omitted config is left
    // unchanged by the API) while creates omit the object so no disabled config record is persisted.
    peakSaverConfiguration: data.peakSaverEnabled
      ? {
          enabled: true,
          saveType: data.peakSaverSaveType ?? DEFAULT_PEAK_SAVER_SAVE_TYPE,
          // The 4 kW default only makes sense for POWER; never send it as a percentage.
          quantity:
            data.peakSaverQuantity ??
            ((data.peakSaverSaveType ?? DEFAULT_PEAK_SAVER_SAVE_TYPE) === 'POWER'
              ? DEFAULT_PEAK_SAVER_QUANTITY_KW
              : undefined)
        }
      : intent === 'update'
        ? { enabled: false }
        : undefined,
    // Same enabled/disabled semantics as peakSaverConfiguration: creates omit a disabled config,
    // updates must send enabled: false explicitly to turn it off. REAL_TIME has no period close, so
    // treat the toggle as off even if leftover form state (from a prior cycle / unmounted switch)
    // still says enabled — updates must send { enabled: false } to clear a previously enabled config.
    demandChargeAllocationConfiguration:
      data.billingCycle !== 'REAL_TIME' && data.demandChargeAllocationEnabled
        ? {
            enabled: true,
            allocationMethod: data.demandChargeAllocationMethod ?? DEFAULT_DEMAND_CHARGE_ALLOCATION_METHOD,
            passThroughPercentage:
              data.demandChargePassThroughPercentage ?? DEFAULT_DEMAND_CHARGE_PASS_THROUGH_PERCENTAGE
          }
        : intent === 'update'
          ? { enabled: false }
          : undefined,
    delinquencyEnforcementEnabled: data.delinquencyEnforcementEnabled,
    delinquencyTestType: data.delinquencyEnforcementEnabled ? data.delinquencyTestType : undefined,
    delinquencyThreshold:
      data.delinquencyEnforcementEnabled && data.delinquencyThreshold
        ? data.delinquencyTestType === 'OUTSTANDING_BALANCE'
          ? Math.round(parseToSmallestDenomination(data.delinquencyThreshold))
          : data.delinquencyThreshold
        : undefined,
    prepaymentEnabled: data.prepaymentEnabled,
    initialPrepaymentAmount:
      data.prepaymentEnabled && data.initialPrepaymentAmount
        ? Math.round(parseToSmallestDenomination(data.initialPrepaymentAmount))
        : undefined,
    topOffThreshold:
      data.prepaymentEnabled && data.topOffThreshold
        ? Math.round(parseToSmallestDenomination(data.topOffThreshold))
        : undefined,
    type: data.type,
    category: data.category,
    settlementCurrency: data.settlementCurrency,
    primaryContactName: data.primaryContactName,
    primaryContactEmail: data.primaryContactEmail,
    invoiceTemplateId: data.invoiceTemplateId || undefined,
    language: data.language,
    products: data.products.map(({ id, quantity, tierQuantitySource, entityCountLevel, priceId, effectiveFrom }) => ({
      productId: id,
      quantity: quantity ?? 1,
      // PRICE_DEFAULT means "use the price default"; the API expects the override to be omitted.
      tierQuantitySource:
        tierQuantitySource && tierQuantitySource !== TIER_QUANTITY_SOURCE_PRICE_DEFAULT
          ? tierQuantitySource
          : undefined,
      // AUTOMATIC means "infer from the product's attached assets"; the API expects the level to be omitted.
      // The level only applies to ENTITY_COUNT billing, so drop it when a non-entity-count override is
      // selected (PRICE_DEFAULT stays: the price default may still resolve to ENTITY_COUNT).
      entityCountLevel:
        entityCountLevel &&
        entityCountLevel !== ENTITY_COUNT_LEVEL_AUTOMATIC &&
        (!tierQuantitySource ||
          tierQuantitySource === TIER_QUANTITY_SOURCE_PRICE_DEFAULT ||
          tierQuantitySource === 'ENTITY_COUNT')
          ? entityCountLevel
          : undefined,
      priceId: priceId || undefined,
      effectiveFrom:
        effectiveFrom && dayjs(effectiveFrom).isValid() ? toStartOfDayIso(effectiveFrom, tzDayjs) : undefined,
      assets: [],
      authorizedEntities
    })),
    discounts,
    commitments,
    // Always send metadata so updates can clear existing entries (API replaces when provided).
    metadata: parseMetadataEntriesToObject(data.metadataEntries) ?? {}
  }
}

/**
 * Converts AgreementResponse to AgreementFormData for form editing
 */
export function parseAgreementResponseToFormData(agreement: AgreementResponse): AgreementFormData {
  const { authorizedFleets, authorizedVehicles, authorizedUsers, authorizedUserGroups, authorizedAuthTags } =
    parseAgreementEntities(agreement)

  // Parse discounts
  const volumeDiscountEnabled = hasAgreementDiscounts(agreement)
  const discounts = volumeDiscountEnabled
    ? agreement.discounts?.map((discount) => {
        // Map quantityUnit, defaulting MILES to KWH since MILES is not fully supported in UI
        const quantityUnit = (discount.quantityUnit === 'MINUTES' ? 'MINUTES' : 'KWH') as 'KWH' | 'MINUTES'
        const application = discount.application ?? 'VOLUME'
        if (application === 'COURTESY') {
          // The period tiers unfold into the courtesy fields; the tier table stays at its empty default.
          return {
            name: discount.name,
            productId: discount.applicableProductIds?.[0],
            application,
            quantityUnit,
            tiers: [{ usageAmountStart: 0 }],
            courtesyDailyQuantity: courtesyQuantityFor(discount.tiers, 'DAY'),
            courtesyWeeklyQuantity: courtesyQuantityFor(discount.tiers, 'WEEK'),
            courtesyPercent: discount.tiers?.[0]?.discountAmount ?? DEFAULT_COURTESY_PERCENT,
            courtesyWeekStartDay: discount.weekStartDay ?? DEFAULT_COURTESY_WEEK_START_DAY
          }
        }
        return {
          name: discount.name,
          productId: discount.applicableProductIds?.[0],
          application,
          quantityUnit,
          tiers: discount.tiers?.map((tier) => ({
            discountAmount: tier.discountAmount,
            usageAmountStart: tier.startQuantity,
            usageAmountEnd: tier.endQuantity
          })) || [{ usageAmountStart: 0 }]
        }
      })
    : [{ name: '', application: 'VOLUME' as const, quantityUnit: 'KWH' as const, tiers: [{ usageAmountStart: 0 }] }]

  // Parse commitments
  const usageCommitmentEnabled = hasAgreementCommitments(agreement)
  const commitments = usageCommitmentEnabled
    ? agreement.commitments?.map((commitment) => ({
        name: commitment.name,
        productId: commitment.productIds?.[0],
        usageAmount: commitment.usageQuantity,
        rateAboveCommitment: commitment.costAmount
      }))
    : [{ name: undefined, productId: undefined, usageAmount: undefined, rateAboveCommitment: undefined }]

  return {
    name: agreement.name ?? '',
    partnerOrganizationId: agreement.payorOrganizationId ?? '',
    type: agreement.type ?? 'PRIVATE',
    category: (agreement.category ?? 'FLEET') as AgreementCategoryV1,
    settlementCurrency: agreement.settlementCurrency ?? 'USD',
    primaryContactName: agreement.primaryContactName ?? '',
    primaryContactEmail: agreement.primaryContactEmail ?? '',
    invoiceTemplateId: agreement.invoiceTemplateId ?? '',
    language: agreement.language ?? 'ENGLISH',

    // Ended assignments (`isActive === false`) are historical. The edit form only manages current
    // attachments; keeping them would trip the archived-product guard on products that are no
    // longer attached. Same filter as integration agreement setup.
    products: (() => {
      const currentAssignments = (agreement.products ?? []).filter((assignment) => assignment.isActive !== false)
      if (currentAssignments.length === 0) {
        return [createEmptyAgreementProductLine()]
      }
      return currentAssignments.map(
        ({ productId, quantity, tierQuantitySource, entityCountLevel, priceId, effectiveFrom }) => ({
          id: productId ?? '',
          quantity: quantity ?? 1,
          tierQuantitySource: tierQuantitySource ?? TIER_QUANTITY_SOURCE_PRICE_DEFAULT,
          entityCountLevel: entityCountLevel ?? ENTITY_COUNT_LEVEL_AUTOMATIC,
          priceId: priceId ?? '',
          effectiveFrom: effectiveFrom ?? ''
        })
      )
    })(),

    authorizedFleets,
    authorizedVehicles,
    authorizedUsers,
    authorizedUserGroups,
    authorizedAuthTags,

    billingCycle: (agreement.billingCycle ?? 'MONTHLY') as BillingCycleV1,
    collectionPolicy:
      getForcedCollectionPolicy(agreement.billingCycle) ?? agreement.collectionPolicy ?? 'SYNOP_COLLECTS',
    lineItemGrouping: agreement.lineItemGrouping ?? '',
    startDate: agreement.startDate ?? '',
    endDate: agreement.endDate || null,
    autoCharge: getForcedAutoCharge(agreement.billingCycle) ?? agreement.autoCharge ?? false,
    reviewFirstInvoices: agreement.reviewFirstInvoices ?? 1,
    invoiceDueDays: agreement.invoiceDueDays,
    billingAnchorDayOfMonth: agreement.billingAnchorDayOfMonth,
    billingAnchorDayOfWeek: agreement.billingAnchorDayOfWeek,
    authorizationChargeEnabled: agreement.authorizationChargeEnabled || false,
    authorizationCharge: agreement.authorizationCharge
      ? parseFromSmallestDenomination(agreement.authorizationCharge)
      : undefined,
    peakSaverEnabled: agreement.peakSaverConfiguration?.enabled ?? agreement.peakSaverEnabled ?? false,
    peakSaverSaveType: agreement.peakSaverConfiguration?.saveType ?? DEFAULT_PEAK_SAVER_SAVE_TYPE,
    // The 4 kW default only applies to POWER (legacy boolean-only agreements); a PERCENTAGE
    // configuration missing its quantity must stay empty so the operator sets it explicitly.
    peakSaverQuantity:
      agreement.peakSaverConfiguration?.quantity ??
      ((agreement.peakSaverConfiguration?.saveType ?? DEFAULT_PEAK_SAVER_SAVE_TYPE) === 'POWER'
        ? DEFAULT_PEAK_SAVER_QUANTITY_KW
        : undefined),
    demandChargeAllocationEnabled: agreement.demandChargeAllocationConfiguration?.enabled ?? false,
    demandChargeAllocationMethod:
      agreement.demandChargeAllocationConfiguration?.allocationMethod ?? DEFAULT_DEMAND_CHARGE_ALLOCATION_METHOD,
    demandChargePassThroughPercentage:
      agreement.demandChargeAllocationConfiguration?.passThroughPercentage ??
      DEFAULT_DEMAND_CHARGE_PASS_THROUGH_PERCENTAGE,
    delinquencyEnforcementEnabled: agreement.delinquencyEnforcementEnabled || false,
    delinquencyTestType: agreement.delinquencyTestType,
    delinquencyThreshold: agreement.delinquencyThreshold
      ? agreement.delinquencyTestType === 'OUTSTANDING_BALANCE'
        ? parseFromSmallestDenomination(agreement.delinquencyThreshold)
        : agreement.delinquencyThreshold
      : undefined,

    volumeDiscountEnabled,
    discounts,

    usageCommitmentEnabled,
    commitments,

    prepaymentEnabled: agreement.prepaymentEnabled || false,
    initialPrepaymentAmount: agreement.initialPrepaymentAmount
      ? parseFromSmallestDenomination(agreement.initialPrepaymentAmount)
      : undefined,
    topOffThreshold: agreement.topOffThreshold ? parseFromSmallestDenomination(agreement.topOffThreshold) : undefined,

    metadataEntries: parseMetadataObjectToEntries(agreement.metadata)
  }
}

export function hasAgreementDiscounts(
  agreement: AgreementResponse
): agreement is AgreementResponse & { discounts: AgreementDiscountResponse[] } {
  return Boolean(agreement.discounts?.length)
}

export function hasAgreementCommitments(
  agreement: AgreementResponse
): agreement is AgreementResponse & { commitments: AgreementCommitmentResponse[] } {
  return Boolean(agreement.commitments?.length)
}

/**
 * Integration agreements are created by the integration setup flow. The agreement
 * wizard cannot edit them, so listing selection and the detail Edit / Unarchive
 * actions stay off.
 */
export function isIntegrationAgreement(agreement: Pick<AgreementResponse, 'category'>): boolean {
  return agreement.category === 'INTEGRATION'
}

export function parseAgreementEntities(agreement: AgreementResponse) {
  // Parse authorized entities by type
  const authorizedFleets: string[] = []
  const authorizedVehicles: string[] = []
  const authorizedUsers: string[] = []
  const authorizedUserGroups: string[] = []
  const authorizedAuthTags: string[] = []

  const seenEntityIds = new Set<string>()
  const allEntities: AgreementAuthorizedEntityResponse[] = []

  agreement.products?.forEach((product) => {
    product.authorizedEntities?.forEach((entity) => {
      const { entityId, entityType } = entity
      if (!entityId || seenEntityIds.has(entityId)) return

      seenEntityIds.add(entityId)
      allEntities.push(entity)

      switch (entityType) {
        case 'FLEET':
          authorizedFleets.push(entityId)
          break
        case 'VEHICLE':
          authorizedVehicles.push(entityId)
          break
        case 'USER':
          authorizedUsers.push(entityId)
          break
        case 'USER_GROUP':
          authorizedUserGroups.push(entityId)
          break
        case 'AUTH_TAG':
          authorizedAuthTags.push(entityId)
          break
      }
    })
  })

  return {
    allEntities,
    authorizedFleets,
    authorizedVehicles,
    authorizedUsers,
    authorizedUserGroups,
    authorizedAuthTags
  }
}

/**
 * Current assignments whose catalog product is ARCHIVED. Unknown or omitted statuses are
 * not treated as archived — the list endpoint may lag or omit status, and a false warning
 * would be worse than a miss. Ended assignments (`isActive === false`) are ignored.
 */
export function getArchivedAssignedProducts(
  products: AgreementProductResponse[] | undefined,
  statusByProductId: ReadonlyMap<string, ProductResponse['status']>
): AgreementProductResponse[] {
  const seen = new Set<string>()
  const archived: AgreementProductResponse[] = []
  for (const assignment of products ?? []) {
    if (assignment.isActive === false || !assignment.productId) {
      continue
    }
    if (statusByProductId.get(assignment.productId) !== 'ARCHIVED') {
      continue
    }
    if (seen.has(assignment.productId)) {
      continue
    }
    seen.add(assignment.productId)
    archived.push(assignment)
  }
  return archived
}

/**
 * True when a current assignment points at an ARCHIVED catalog product.
 * Includes archived agreements so the listing badge still flags blockers before unarchive.
 */
export function hasArchivedAssignedProduct(
  products: AgreementProductResponse[] | undefined,
  statusByProductId: ReadonlyMap<string, ProductResponse['status']>
): boolean {
  return getArchivedAssignedProducts(products, statusByProductId).length > 0
}

/**
 * `isArchivedProduct` is written by the Choose Products step and is not on the API payload.
 * Preserve it across `reset` when the same product id is still in that slot so a cache refetch
 * cannot wipe the flag and fail final submit after the user has left that step.
 */
export function mergePreservedProductActivity(
  formData: AgreementFormData,
  currentProducts: AgreementProductFormData[]
): AgreementFormData {
  return {
    ...formData,
    products: formData.products.map((product, index) => {
      const current = currentProducts[index]
      if (current?.id === product.id && typeof current.isArchivedProduct === 'boolean') {
        return { ...product, isArchivedProduct: current.isArchivedProduct }
      }
      return product
    })
  }
}
